/**
 * Top-header workspace switcher (#1192 → #1369 → #1493).
 *
 * Admins have a flag-superset of every back-office permission preset, so an
 * admin can already reach every workspace by URL today (the route guards in
 * ProtectedRoute pass for an admin). What was missing was *discoverability*:
 * #1192/#1263 only ever offered a two-segment Admin ⇄ CRM toggle. #1369 evolves
 * that toggle into a compact **workspace dropdown** so an admin can jump to ANY
 * workspace — Super User, Agent CRM, Aftersales/Paralegal, Finance/Accounts (#1506),
 * Management, Administration — from one menu.
 *
 * Wayfinding only (#1369): this changes NO permissions and NO route guards. An
 * admin opens these surfaces with their own (superset) privileges — there is no
 * "view-as-persona" masking here (that is a separate, security-sensitive
 * feature, explicitly out of scope). The route guards are untouched.
 *
 * #1263 gate UNCHANGED: the switcher is ADMIN-ONLY. Every non-admin (plain field
 * agents AND the back-office permission presets — paralegal / finance / manager
 * / accounts) has a single scoped home and renders no switcher at all. This is
 * what satisfies "the other dashboards should not be able to switch".
 *
 * Persistence semantics UNCHANGED (#1192): `setViewPreference('home'|'crm')`
 * continues to govern ONLY the index `/` (`RoleRedirect`) landing — Admin vs the
 * agent CRM. Visiting paralegal/finance/etc. is transient wayfinding; we
 * deliberately do NOT persist a back-office landing (the enum stays 'home'|'crm'),
 * so `/` never strands an admin in a back-office queue.
 *
 * #1358 behaviour preserved: selecting an item ALWAYS navigates to that
 * workspace's home — including the current workspace — so there are no dead
 * clicks (only an exact match on the destination path is skipped, as there is
 * nowhere to go).
 *
 * Style: Tailwind utilities, Inter, Lucide icons, dark-mode aware, ≥44px touch
 * targets (WCAG 2.5.5). The trigger collapses to icon-only on very small
 * screens. Real menu semantics (aria-haspopup/expanded, keyboard nav, Escape,
 * click-outside). `data-tour="view-switcher"` stays on the root (tours anchor
 * to it).
 *
 * #1405 → #1493 (viewport-aware placement): the menu must never open offscreen,
 * and it must stay ANCHORED TO ITS TRIGGER at every viewport. #1405 first fixed
 * the offscreen bug with a mobile bottom-sheet (`fixed inset-x-0 bottom-0`), but
 * that detached the menu from the toggle (which lives top-left in the header) —
 * Natalie reported it felt disconnected (#1493). #1493 replaces the bottom-sheet
 * with a SINGLE anchored popover that opens directly beneath the trigger on ALL
 * viewports:
 *   - Anchored `absolute` to the relative root, LEFT-aligned to the trigger
 *     (which sits near the left of the header), so it never runs off the left
 *     edge the way the original `right-0` anchor did.
 *   - Width-clamped to the viewport (`w-[min(92vw,14rem)]`, the established
 *     TourTooltip clamp) so it can never overflow the RIGHT edge down to ~320px.
 *   - Flips ABOVE the trigger only when there is genuinely no room below
 *     (measured from the trigger's rect vs the viewport height on open + on
 *     resize/scroll); height-capped + scrollable so a tall menu still fits.
 * Anchoring beneath the trigger + a vw-relative width is the load-bearing
 * offscreen guard — the #1405 bug cannot regress.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Scale,
  Wallet,
  Gavel,
  UserCog,
  ShieldCheck,
  ChevronDown,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "../../contexts/auth";
import { usePreviewPersonaOptional } from "../../contexts/previewPersona";
import type { PreviewPersonaKey } from "../../lib/previewPersona";
import { setViewPreference } from "../../lib/viewPreference";

/**
 * A switchable workspace. `pref` is the persisted view preference to write when
 * selected — ONLY Admin ('home') and Agent CRM ('crm') govern the `/` landing,
 * so the back-office workspaces carry `pref: null` (visiting them is transient,
 * the enum is never extended — see header note).
 */
interface Workspace {
  key: string;
  label: string;
  /** The workspace's home path (the navigation target). */
  path: string;
  icon: LucideIcon;
  /** Persisted preference to set on select, or null (transient back-office). */
  pref: "home" | "crm" | null;
  /** Route prefixes that mean "you are currently in this workspace". */
  prefixes: string[];
  /**
   * The view-as-persona preview key this workspace enters on select (#1383).
   * Null for the Admin workspace -- selecting it EXITS any preview (the
   * full-admin baseline).
   */
  persona: PreviewPersonaKey | null;
}

// The admin workspace is the fallthrough "current" (see deriveCurrent) and is
// named so the derivation returns a definitely-defined Workspace (no indexed
// access under noUncheckedIndexedAccess).
const ADMIN_WORKSPACE: Workspace = {
  key: "admin",
  label: "Super User",
  path: "/admin/dashboard",
  icon: LayoutDashboard,
  pref: "home",
  prefixes: [], // the fallthrough "current" — see deriveCurrent
  persona: null, // selecting Admin EXITS preview (full-admin baseline)
};

// Order: Admin first (the admin's home), then the agent CRM, then the four
// back-office workspaces.
const WORKSPACES: Workspace[] = [
  ADMIN_WORKSPACE,
  {
    key: "crm",
    label: "Agent CRM",
    path: "/crm/dashboard",
    icon: Building2,
    pref: "crm",
    prefixes: ["/crm"],
    persona: "crm",
  },
  {
    key: "paralegal",
    label: "Aftersales/Paralegal",
    path: "/paralegal",
    icon: Scale,
    pref: null,
    prefixes: ["/paralegal", "/legal"],
    persona: "paralegal",
  },
  {
    // #1506: Finance and Accounts are the SAME person at LWP -- ONE combined
    // workspace. Selecting it lands on /finance-accounts, which surfaces BOTH
    // the commission-release (Finance) and deposit-to-invest (Accounts)
    // functions. The underlying /finance and /accounts routes still work, so
    // both prefixes (and the combined one) reflect this workspace as "current".
    key: "finance_accounts",
    label: "Finance/Accounts",
    path: "/finance-accounts",
    icon: Wallet,
    pref: null,
    prefixes: ["/finance-accounts", "/finance", "/accounts"],
    persona: "finance_accounts",
  },
  {
    key: "manager",
    label: "Management",
    // #1688: renamed /manage-signoff -> /management (label match). The old path
    // is kept as a current-workspace prefix so the redirect shim (and any stale
    // deep link in flight) still reflects this workspace as active.
    path: "/management",
    icon: Gavel,
    pref: null,
    prefixes: ["/management", "/manage-signoff"],
    persona: "manager",
  },
  {
    // Office Admin (#1299) lands on /crm/dashboard like a plain agent, so it
    // carries NO route prefixes (deriveCurrent must not steal /crm/* from the
    // Agent CRM workspace when NOT previewing). While PREVIEWING-as office_admin
    // the trigger reflects it via the persona lookup in `current`. Its masked
    // view differs from a plain agent (no money / own-book money, scoped user
    // management only), so it is a distinct preview.
    key: "office_admin",
    label: "Administration",
    path: "/crm/dashboard",
    icon: UserCog,
    pref: null,
    prefixes: [],
    persona: "office_admin",
  },
  {
    // D5-RBP-005 (#4175): the combined HR/Paralegal desk lands on /paralegal
    // (the paralegal workspace owns that prefix, so this one carries none --
    // like Administration, it reflects as current only while previewing).
    // Its masked view adds the HR compliance roster to the paralegal desk.
    key: "hr_paralegal",
    label: "HR/Paralegal",
    path: "/paralegal",
    icon: ShieldCheck,
    pref: null,
    prefixes: [],
    persona: "hr_paralegal",
  },
];

/**
 * Which workspace the current route belongs to. Matches by prefix; Admin is the
 * fallthrough (anything not under another workspace's prefix — e.g. /admin/* — is
 * the admin workspace).
 */
function deriveCurrent(pathname: string): Workspace {
  for (const ws of WORKSPACES) {
    if (
      ws.prefixes.some(
        (p) => pathname === p || pathname.startsWith(p + "/"),
      )
    ) {
      return ws;
    }
  }
  // Admin is the fallthrough home workspace.
  return ADMIN_WORKSPACE;
}

export function ViewSwitcher() {
  const { user } = useAuth();
  const { persona: previewPersona, enterPreview, exitPreview } =
    usePreviewPersonaOptional();
  const location = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  // #1493: flip the popover ABOVE the trigger only when there is no room below.
  const [flipUp, setFlipUp] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // #1263: ADMIN-ONLY. Every non-admin (plain agents AND back-office presets)
  // sees no switcher — they each have a single scoped home. This hook order is
  // safe because the early return is below all hooks.
  const isAdmin = user?.role === "admin";

  // Current workspace: while previewing-as a persona (#1383) the trigger reflects
  // THAT persona (even on a shared page like /crm/* that the persona reaches),
  // so the admin always sees which persona they are previewing. Otherwise it is
  // derived from the active route (where the admin actually is).
  const current = previewPersona
    ? (WORKSPACES.find((w) => w.persona === previewPersona) ??
      deriveCurrent(location.pathname))
    : deriveCurrent(location.pathname);

  // Close on click-outside.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  // #1493: decide whether to open the popover below (default) or flip it above
  // the trigger. The trigger lives in the header (top of the viewport), so below
  // is almost always correct — but if the header ever sits low and there is more
  // room above than below, flip up so the menu is never pushed off the bottom.
  // Measured on open and kept in sync on resize/scroll while open.
  useLayoutEffect(() => {
    if (!open) {
      setFlipUp(false);
      return;
    }
    function place() {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      // Estimated menu height (7 items × 44px + a little chrome). We only flip
      // when there is genuinely no room below AND more room above.
      const estimated = 360;
      setFlipUp(spaceBelow < estimated && spaceAbove > spaceBelow);
    }
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  if (!isAdmin) {
    return null;
  }

  function select(ws: Workspace) {
    setOpen(false);
    triggerRef.current?.focus();

    // #1383: enter (or exit) the view-as-persona PREVIEW. Selecting a non-admin
    // workspace enters that persona's preview so the nav + pages mask to exactly
    // what their real login renders; selecting Admin (persona null) exits the
    // preview back to full admin. This is frontend masking only.
    //
    // #1392: the preview flip happens BEFORE the navigation so the destination
    // route mounts with the TARGET persona's effective flags already in place --
    // navigating first briefly mounted the destination guard with the OUTGOING
    // persona's flags and flashed that guard's 403 ("the finance queue is not
    // available...") for a frame. With the preview applied first, the source
    // admin-only route (/admin/*) momentarily sees effectiveRole === "agent",
    // but AdminRoute (ProtectedRoute.tsx) now REDIRECTS a previewing admin to
    // their persona landing rather than rendering 403 -- so there is no source
    // flash either. Both flash classes are gone.
    if (ws.persona) {
      enterPreview(ws.persona);
    } else {
      exitPreview();
    }

    // Persist ONLY for Admin/CRM (the two that govern the `/` landing). The
    // back-office workspaces are transient -- the enum is never extended.
    if (ws.pref) {
      setViewPreference(ws.pref);
    }

    // #1358: no dead clicks -- selecting the current workspace still navigates to
    // its home, unless we are EXACTLY on that home already (nowhere to go).
    if (location.pathname !== ws.path) {
      navigate(ws.path);
    }
  }

  function onTriggerKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
      // Focus the first menu item on the next paint.
      requestAnimationFrame(() => {
        const first = rootRef.current?.querySelector<HTMLElement>(
          '[role="menuitem"]',
        );
        first?.focus();
      });
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  function onMenuKeyDown(e: React.KeyboardEvent) {
    const items = Array.from(
      rootRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    const idx = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      items[(idx + 1) % items.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus();
    }
  }

  const CurrentIcon = current.icon;

  return (
    <div
      ref={rootRef}
      data-tour="view-switcher"
      className="relative inline-block text-left"
    >
      {/* #1897: the trigger carries an explicit aria-label. Its icon + chevron
          are aria-hidden and the visible label is hidden below sm:, so on mobile
          this control is otherwise icon-only -- the aria-label names it on every
          viewport (and supersedes the previous sr-only span). */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Switch workspace — currently ${current.label}`}
        title={`${current.label} — switch workspace`}
        className="inline-flex items-center gap-1.5 min-h-[44px] px-2.5 sm:px-3 rounded-lg text-sm font-medium border border-[var(--color-border)] bg-[var(--color-bg-page)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-card)] transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent"
      >
        <CurrentIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="hidden sm:inline whitespace-nowrap">
          {current.label}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Switch workspace"
          onKeyDown={onMenuKeyDown}
          className={
            // #1493: a single anchored popover beneath the trigger at EVERY
            // viewport (no bottom-sheet). Absolutely positioned against the
            // relative root, LEFT-aligned to the trigger (which sits near the
            // left of the header), and width-clamped to the viewport so it can
            // never overflow either edge down to ~320px.
            "absolute left-0 right-auto z-50 w-[min(92vw,14rem)] " +
            "max-h-[80vh] overflow-y-auto rounded-lg border " +
            "border-[var(--color-border)] bg-[var(--color-bg-card)] " +
            "py-1 shadow-lg focus:outline-hidden " +
            // Flip above only when there is no room below (#1493); default below.
            (flipUp
              ? "bottom-full mb-1.5 origin-bottom-left"
              : "top-full mt-1.5 origin-top-left")
          }
        >
          {WORKSPACES.map((ws) => {
            const Icon = ws.icon;
            const active = ws.key === current.key;
            return (
              <button
                key={ws.key}
                type="button"
                role="menuitem"
                onClick={() => select(ws)}
                aria-current={active ? "true" : undefined}
                className={`flex w-full items-center gap-2.5 min-h-[44px] px-3 text-left text-sm transition-colors focus:outline-hidden focus-visible:bg-[var(--color-bg-page)] ${
                  active
                    ? "font-semibold text-brand-accent bg-[var(--color-bg-page)]"
                    : "text-[var(--color-text-primary)] hover:bg-[var(--color-bg-page)]"
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="whitespace-nowrap">{ws.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
